import ExtendedWorkItem from '@Components/WorkItemV2/ExtendedWorkItem'
import BulkActionCheckbox from '@Components/WorkItemV2/BulkActionCheckbox'
import React, { useContext } from 'react'
import { StoresContext } from '@App/contexts'
import { WorkModel } from '@Entities/work/work'
import ShareDisplay from '@Components/WorkItemV2/ShareDisplay'
import dayjs from 'dayjs'
import { OverclaimShareDisplayHelper } from '@App/helpers/overclaim-share-display-helper'
import './index.scss'
import Navigation from '@App/navigation'
import CustomToolTip from '@Components/Tooltips/CustomToolTip'
import { isOverclaimHelper } from '@App/helpers/is-overclaim-helper'
import { observer } from 'mobx-react'

interface OverclaimExtendedWorkItemProps {
    work: WorkModel
    toCatalog?: boolean
}

const OverclaimExtendedWorkItem = (props: OverclaimExtendedWorkItemProps) => {
    const { rightsholderStore, overclaimSearchBulkActionStore } = useContext(StoresContext)
    const { work, toCatalog } = props
    const totalOverclaim = OverclaimShareDisplayHelper(work.totalKnownShares)

    const path = toCatalog
        ? Navigation.rightholder.catalog.work(rightsholderStore.currentRightsholderId, work.propertyId ?? 0)
        : Navigation.rightholder.overclaim.view(
              rightsholderStore.currentRightsholderId,
              work.overclaimHeaderId as number
          )

    const limitReached =
        overclaimSearchBulkActionStore.idsSelectedForBulkAction.size > 100 &&
        !overclaimSearchBulkActionStore.idsSelectedForBulkAction.has(work.overclaimHeaderId as number)

    const bulkActionDisabled = React.useMemo(() => {
        if (work.relinquished) {
            return { value: true, message: 'This work has been relinquished and cannot be selected.' }
        }
        if (!isOverclaimHelper(work)) {
            return { value: true, message: 'This work is not in overclaim status and cannot be selected.' }
        }
        if (limitReached) {
            return { value: true, message: 'You can only select up to 100 works at a time.' }
        }
        return { value: false, message: '' }
    }, [work.relinquished, work.overclaimStatusFilter, limitReached])

    const fields = [
        {
            title: 'Date Created',
            value: dayjs(work.overclaimCreationDate ?? '')
                .utc()
                .format('MMMM DD, YYYY'),
            tooltip: 'This is the date when the work went into overclaim.',
        },
        { title: 'MLC Song Code', value: work.songCode },
        {
            title: 'Overclaim ID',
            value: work.overclaimHeaderId?.toString(),
        },
    ]

    const secondaryFields = [
        {
            title: 'Last Updated',
            value: dayjs(work.lastUpdated ?? '')
                .utc()
                .format('MMMM DD, YYYY'),
            tooltip: 'The most recent date when an update was made to this work.',
        },
    ]

    const isBulkActionStarted = overclaimSearchBulkActionStore.activeBulkAction !== null

    return (
        <div className="overclaim-extended-work-item-wrapper">
            <ExtendedWorkItem
                work={work}
                catalogPath={path}
                fields={fields}
                secondaryFields={secondaryFields}
                showOverclaimStatus
                isBulkActionStarted={isBulkActionStarted}
                isBulkActionDisabled={bulkActionDisabled.value}
                selectionControl={
                    isBulkActionStarted && (
                        <BulkActionCheckbox
                            id={work.id?.toString() || ''}
                            checked={
                                !!work.overclaimHeaderId &&
                                overclaimSearchBulkActionStore.idsSelectedForBulkAction.has(
                                    work.overclaimHeaderId as number
                                )
                            }
                            onToggle={() =>
                                overclaimSearchBulkActionStore.toggleSelectionForBulkAction(work.overclaimHeaderId)
                            }
                            disabled={bulkActionDisabled.value}
                            disabledMessage={bulkActionDisabled.message}
                        />
                    )
                }
                openInNewTab={overclaimSearchBulkActionStore.isAmendmentTableOpen}
            >
                <div className="submitted-shares">
                    <CustomToolTip
                        title="Submitted Shares"
                        info="The total percentage of shares currently being claimed on this work."
                    />
                    <ShareDisplay share={work.totalKnownShares} overclaim />
                </div>

                {work.overclaimStatusFilter !== 'RESOLVED' && (
                    <div className="total-overclaim">
                        <CustomToolTip title="Total Overclaim" info="The percentage of shares that is over 100%." />
                        <ShareDisplay share={totalOverclaim} highlight overclaim />
                    </div>
                )}
            </ExtendedWorkItem>
        </div>
    )
}
export default observer(OverclaimExtendedWorkItem)
